// MonthViewDayCell.jsx

import React from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { resolveAppdeckEventColors } from 'views/calendar/shared/calendarEventColors';
import {
  getCalendarDateKey,
  getCalendarEventMetaLabel,
  getCalendarEventTimeLabel,
  getCalendarEventTooltip,
  getCalendarNodeTitle,
} from 'views/calendar/shared/calendarUtils';

// CSS
import './MonthViewDayCell.css';

/**
 * MonthViewDayCell
 *
 * @returns {ReactNode} The rendered component.
 */
const MonthViewDayCell = ({
  day,
  items,
  language = 'en',
  onNodeClick,
  onNodeDoubleClick,
  onAddDate,
  canDragEvents = false,
  draggingItem = null,
  dragOverTargetKey = null,
  onEventDragStart,
  onEventDragEnd,
  onDropTargetDragOver,
  onMoveToTarget,
}) => {

  // ----- CONTEXT ----- //

  const { theme } = useTheme();
  const dropTargetKey = `day:${getCalendarDateKey(day.date)}`;
  const isDropTarget = dragOverTargetKey === dropTargetKey;

  // ----- RENDER ----- //

  return (
    <div
      className={[
        'month-view-day',
        day.inMonth ? '' : 'month-view-day--outside',
        isDropTarget ? 'month-view-day--drop-target' : '',
      ].filter(Boolean).join(' ')}
      style={{
        backgroundColor: isDropTarget
          ? theme.backgroundHover
          : (day.inMonth ? theme.panel : theme.panel),
        borderBottomColor: theme.backgroundMuted,
        borderRightColor: theme.backgroundMuted,
      }}
      onClick={() => {
        if (draggingItem) return;
        onAddDate?.(day.date);
      }}
      onDragOver={(event) => {
        if (!canDragEvents || !draggingItem) return;
        onDropTargetDragOver?.(dropTargetKey, event);
      }}
      onDrop={(event) => {
        if (!canDragEvents) return;
        event.preventDefault();
        event.stopPropagation();
        onMoveToTarget?.(day.date, { targetKey: dropTargetKey });
      }}
    >

      {/* DAY HEADER */}
      <div className="month-view-day-header">

        {/* DAY NUMBER */}
        <span
          className={[
            'month-view-day-number',
            day.isToday ? 'month-view-day-number--today' : '',
          ].filter(Boolean).join(' ')}
          style={{
            backgroundColor: day.isToday ? theme.highlight : 'transparent',
            color: day.isToday ? theme.highlightForeground : theme.foregroundMuted,
          }}
        >
          {day.date.getDate()}
        </span>
      </div>

      {/* EVENTS */}
      <div className="month-view-events">
        {items.map((item, index) => {
          const title = getCalendarNodeTitle(item.node);
          const metaLabel = getCalendarEventMetaLabel(item);
          const timeLabel = getCalendarEventTimeLabel(item, language);
          const tooltip = getCalendarEventTooltip(item, language);
          const eventColors = resolveAppdeckEventColors(item, index);

          return (
            <div
              className={[
                'month-view-event',
                draggingItem?.node?.key === item.node?.key
                && draggingItem?.field?.name === item.field?.name
                  ? 'month-view-event--dragging'
                  : '',
              ].filter(Boolean).join(' ')}
              style={{
                backgroundColor: eventColors.background,
                color: eventColors.foreground,
              }}
              key={`${item.node?.key || 'node'}-${item.field?.name || 'date'}-${item.occurrenceDateKey || item.date?.getTime?.() || index}`}
              title={tooltip}
              onClick={(event) => {
                event.stopPropagation();
                onNodeClick?.(event, item);
              }}
              onDoubleClick={(event) => {
                event.stopPropagation();
                onNodeDoubleClick?.(event, item);
              }}
              draggable={canDragEvents && !item?.isRecurringOccurrence && !item?.rawValue?.recurrence}
              onDragEnd={() => onEventDragEnd?.()}
              onDragStart={(event) => onEventDragStart?.(item, event)}
            >

              {/* EVENT TITLE */}
              <div
                className="month-view-event-title"
                style={{
                  color: eventColors.foreground,
                }}
              >
                {title}
              </div>

              {/* EVENT FIELD SUBTITLE */}
              {metaLabel ? (
                <div
                  className="month-view-event-subtitle"
                  style={{
                    color: eventColors.foreground,
                  }}
                >
                  {metaLabel}
                </div>
              ) : null}

              {/* EVENT TIME SUBTITLE */}
              {timeLabel ? (
                <div
                  className="month-view-event-subtitle"
                  style={{
                    color: eventColors.foreground,
                  }}
                >
                  {timeLabel}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Dev Tools
MonthViewDayCell.displayName = 'MonthViewDayCell';

export default MonthViewDayCell;
